import { formatPrice, formatPriceInput } from '@dashboard/services/formatting';
import getSubscriptionIntervalWithSlash from '@dashboard/services/formatting/getSubscriptionIntervalWithSlash';
import { getPriceBillingConfig } from '@repo/checkout';
import { trimmed } from '@repo/lib';
import draftToNestedPrice from '../../lib/draftToNestedPrice';

/**
 * Billing config for a price in the create/edit form. Label helpers render both
 * SAVED prices (nested recurring/paymentPlan) and flat Formik drafts, but
 * getPriceBillingConfig only reads the nested shape — a newly added price is a
 * flat draft (interval/intervalCount/planIterations at the top level, nested
 * only at submit). Normalize the draft to nested first so the header shows the
 * interval and the "Payment plan" label before Save. Idempotent on already-nested
 * prices.
 */
export function getDraftBillingConfig(price) {
  return getPriceBillingConfig(draftToNestedPrice(price));
}

/**
 * Count enabled prices regardless of whether an amount has been entered yet.
 *
 * The moment a second enabled price exists the buyer will see a picker, even
 * before its amount is filled in. Counting by amount instead would flicker
 * dependent UI (the "Preselected" star, Label/Description gating) while a
 * sibling's amount is cleared mid-edit. Shared by PriceForm and PriceCard.
 */
export function countEnabledPrices(prices) {
  return (Array.isArray(prices) ? prices : []).filter(p => p.enabled !== false).length;
}

/**
 * Merchant-facing internal id for a price, derived from its own data (SKU-like),
 * e.g. "price_one_time_fixed_9900". `amount` is in the smallest currency unit
 * (cents), matching what the API stores.
 *
 * Mirrors the backend generator (`buildPriceReference` in
 * apps/api/services/ProductService.js) — keep the two in sync so a price's
 * reference doesn't change between the create flow and the save.
 */
export function buildPriceReference({ billingType, payWhatYouWant, amount } = {}) {
  const type = billingType || 'one_time';
  const pricing = payWhatYouWant ? 'pwyw' : 'fixed';
  const value = amount != null ? amount : 0;
  return `price_${type}_${pricing}_${value}`;
}

/**
 * Fill in a reference for any price that doesn't have one yet, so the create flow
 * shows the same value the backend would generate at save (the backend only
 * generates when the reference is unset). Dedupes within the product (_2, _3, …)
 * exactly like the backend.
 *
 * Skips prices with no amount entered yet (and not pay-what-you-want), so we don't
 * bake in a stale "_0" before the merchant types an amount. Form amounts are dollar
 * strings, so they're converted to cents to match the backend reference. Returns
 * the same array reference when nothing changed, so callers can skip a needless
 * setFieldValue.
 */
export function fillMissingPriceReferences(prices, productCurrency) {
  if (!Array.isArray(prices)) return prices;
  // Walk in order and record each reference as it's seen — mirroring the backend
  // loop — so the dedupe suffixes come out identical on both sides.
  const used = new Set();
  let changed = false;
  const next = prices.map(p => {
    const ref = trimmed(p?.reference);
    if (ref) {
      used.add(ref);
      return p;
    }
    const hasAmount = p?.amount != null && String(p.amount).trim() !== '';
    if (!hasAmount && !p?.payWhatYouWant) return p; // amount not entered yet — wait for it
    const currency = p.currency || productCurrency || 'usd';
    const cents = hasAmount ? formatPriceInput(p.amount, currency) : 0;
    const base = buildPriceReference({
      billingType: p.billingType,
      payWhatYouWant: p.payWhatYouWant,
      amount: cents,
    });
    let reference = base;
    let count = 2;
    while (used.has(reference)) {
      reference = `${base}_${count}`;
      count += 1;
    }
    used.add(reference);
    changed = true;
    return { ...p, reference };
  });
  return changed ? next : prices;
}

// Reduce a price to its user-editable, persisted fields for change detection.
// Strip:
//  - UI-only state (radio/disclosure selections, reveal flags, reorder keys) —
//    toggling a reveal isn't a real edit, and
//  - server-managed metadata (ids, references, timestamps, purchase counts) — a
//    Save reseeds the form from the server response (getFormValues spreads the whole
//    price), so `updatedAt` (and createdAt / a server-assigned reference / …) come
//    back changed on every save; counting those would make a just-saved price look
//    dirty again. Matches the server-field set handleDuplicatePrice drops.
// Shared by PriceCard (the "unsaved changes" check) and PricesList (the single-price
// autosave gate) so both judge "did anything real change?" the same way.
export function persistedFields({
  __pricing,
  __schedule,
  __trial,
  __discountOpen,
  __startDateOpen,
  __setupFeeOpen,
  _reorderKey,
  _originalIndex,
  _id,
  key,
  reference,
  order,
  createdAt,
  updatedAt,
  __v,
  hasBeenPurchased,
  chargeCount,
  subscribers,
  ...rest
} = {}) {
  // A number input stores edits as numbers (Formik parses type="number"), but
  // getFormValues seeds the price amounts as formatted strings — so a saved price
  // reseeds as "15" while the open-time snapshot still holds 15. Same value, different
  // type → isEqual would flag a change. Coerce the numeric price fields so they compare
  // by value (this also settles "15.50" vs the reseeded "15.5").
  const num = v => (v === '' || v == null ? '' : Number(v));
  return {
    ...rest,
    amount: num(rest.amount),
    discountedFromPrice: num(rest.discountedFromPrice),
    setupFee: num(rest.setupFee),
    pwywSuggestedPrice: num(rest.pwywSuggestedPrice),
  };
}

/**
 * Reset the UI-only "reveal" flags (`__discountOpen` / `__setupFeeOpen` /
 * `__startDateOpen`) to whether their underlying field actually holds a value,
 * mirroring getFormValues (which reseeds them from the data on every edit-flow
 * save). The create flow never reseeds — prices are built client-side — so a
 * reveal the merchant ticked without entering a value would otherwise stay on.
 * Call this when a create-flow price is committed. Empty or zero amounts count
 * as "no value" (matching the round-trip through the API + getFormValues).
 */
export function normalizeRevealFlags(price) {
  if (!price) return price;
  const hasAmount = v => {
    const n = Number(String(v ?? '').trim());
    return Number.isFinite(n) && n > 0;
  };
  return {
    ...price,
    __discountOpen: hasAmount(price.discountedFromPrice),
    __setupFeeOpen: hasAmount(price.setupFee),
    __startDateOpen: !!price.startDate,
  };
}

/**
 * Human-readable headline for a price, used in pickers and summaries:
 * "Label — $29.00 / month", "$199.00", or "Pay what you want" — prefixed with
 * the price's label when it has one, otherwise built from billing + amount.
 */
export function getPriceLabel(price) {
  if (!price) return '';

  const label = price.label;
  const currency = price.currency || 'usd';

  let headline;
  if (price.payWhatYouWant) {
    headline = 'Pay what you want';
  } else {
    const formatted = formatPrice({ amount: price.amount ?? 0, currency, style: 'currency' });
    const config = getDraftBillingConfig(price);
    headline =
      price.billingType === 'recurring' && config?.interval
        ? `${formatted} ${getSubscriptionIntervalWithSlash(config.interval, config.intervalCount)}`
        : formatted;
  }

  return label ? `${label} — ${headline}` : headline;
}

/**
 * The price's "type" label — its merchant label, else the billing-type name
 * ("One-time" / "Subscription" / "Payment plan"), or "Pay what you want". Shared
 * by PriceCard's header and the preview price selector.
 */
export function getPriceTypeLabel(price) {
  if (!price) return '';
  // Pay-what-you-want shows the merchant's label if set, else "Pay what you want"
  // — the configured min/suggested amount shows in the price headline.
  if (price.payWhatYouWant) return price.label || 'Pay what you want';
  if (price.label) return price.label;
  if (price.billingType === 'one_time') return 'One-time';
  return getDraftBillingConfig(price)?.planIterations > 0 ? 'Payment plan' : 'Subscription';
}

/**
 * The price's pricing-TYPE name — always the leaf type, ignoring any merchant
 * label: 'Pay what you want' | 'One-time payment' | 'Subscription' | 'Payment
 * plan'. Distinct from getPriceTypeLabel (which prefers the custom label); used
 * for the single-price form label and the multi-price card sub-label, where the
 * type is otherwise invisible.
 */
export function getPriceTypeName(price) {
  if (!price) return '';
  if (price.billingType === 'one_time') {
    return price.payWhatYouWant ? 'Pay what you want' : 'One-time payment';
  }
  if (price.payWhatYouWant) return 'Pay what you want';
  return getDraftBillingConfig(price)?.planIterations > 0 ? 'Payment plan' : 'Subscription';
}

/**
 * The price's amount headline: "$20", "$5 / month", or for pay-what-you-want
 * "Suggested $5" / "Min. $5" / "No min. price". Form state holds amounts as
 * dollar strings; saved prices hold cents — pass `amountsInCents: true` for the
 * latter. Shared by PriceCard's header and the preview price selector.
 */
export function getPriceHeadline(price, { amountsInCents = false } = {}) {
  if (!price) return '';
  const currency = price.currency || 'usd';
  const toCents = value =>
    amountsInCents ? Number(value ?? 0) : formatPriceInput(value ?? 0, currency);
  const money = cents => formatPrice({ amount: cents, currency, style: 'currency' });

  const config = getDraftBillingConfig(price);
  const intervalSuffix =
    price.billingType === 'recurring' && config?.interval
      ? ` ${getSubscriptionIntervalWithSlash(config.interval, config.intervalCount)}`
      : '';

  if (price.payWhatYouWant) {
    const suggested = toCents(price.pwywSuggestedPrice);
    if (suggested > 0) return `Suggested ${money(suggested)}${intervalSuffix}`;
    const min = toCents(price.amount);
    if (min > 0) return `Min. ${money(min)}${intervalSuffix}`;
    return `No min. price${intervalSuffix}`;
  }

  return `${money(toCents(price.amount))}${intervalSuffix}`;
}

/**
 * Combined label for the preview price selector, reading SAVED prices (cents):
 * "One-time — $20", "Subscription — $5 / month", "Premium — Suggested $5". Uses an
 * em-dash to match getPriceLabel and the <Dash /> component.
 */
export function getPriceSelectLabel(price) {
  return `${getPriceTypeLabel(price)} — ${getPriceHeadline(price, { amountsInCents: true })}`;
}

/**
 * Whether a price is a fixed price with no real amount (blank or 0). Pay-what-you-want
 * (its minimum can legitimately be 0/blank) and Stripe-managed prices (the amount comes
 * from Stripe, not the local field) are never "blank/zero" here.
 *
 * A fixed 0 is only valid as the SOLE price of a product — either an edit-saved single
 * price or the no-base-price/variants mode. This predicate is the shared gate for both
 * the create-flow transform (a lone blank/0 fixed price becomes no-base-price) and the
 * block that keeps a blank/0 fixed price out of any multi-price arrangement.
 */
export function isBlankOrZeroFixedPrice(price) {
  if (!price) return false;
  if (price.payWhatYouWant) return false;
  if (price.managedInStripe) return false;
  const raw = String(price.amount ?? '').trim();
  return raw === '' || Number(raw) === 0;
}

/**
 * Whether a price should show the "Price must be more than 0" error live (as you
 * edit it), because it's a fixed 0/blank price that isn't allowed to be free.
 *
 * The create flow's always-present trailing draft (`draftId`) is always excluded:
 * it's droppable and gets validated when it's committed ("Add another price"),
 * not while it's still an empty proposal, and a lone blank draft transforms to a
 * no-base-price checkout on "Create checkout".
 *
 * Otherwise:
 *  - Create (`isNew`): every ADDED (non-draft) price must be > 0 — once a price
 *    has been added it can't be edited back to 0, even as the only added price.
 *  - Edit: only blocks when 2+ prices exist. A lone edit price of 0 is a valid
 *    free / no-base-price single price.
 */
export function isMultiPriceZeroBlocked(price, prices, draftId, isNew = false) {
  if (!isBlankOrZeroFixedPrice(price)) return false;
  const id = price?._id || price?.key;
  if (draftId != null && id === draftId) return false;
  if (isNew) return true;
  const committedCount = (Array.isArray(prices) ? prices : []).filter(
    p => (p._id || p.key) !== draftId,
  ).length;
  return committedCount >= 2;
}

/**
 * Whether a price's amount differs from its last-saved (initial) value, matched
 * by id. A price with no saved counterpart (freshly added) counts as changed.
 * Numeric-normalized so "10" / 10 / "10.00" compare equal (only the value moved,
 * not its format).
 */
export function isPriceAmountDirty(price, initialValues) {
  const id = price?._id || price?.key;
  const initial = (initialValues?.prices || []).find(p => (p._id || p.key) === id);
  if (!initial) return true;
  const num = v => (v === '' || v == null ? '' : Number(v));
  return num(price?.amount) !== num(initial.amount);
}

/**
 * Whether to surface the "Price must be more than 0" block for a price: the
 * value-level rule (isMultiPriceZeroBlocked) plus an edit-flow guard — a price
 * loaded at 0 is only flagged once the merchant actually changes its amount, so
 * opening the page editor never shows an error they didn't cause. The create
 * flow flags added prices immediately (there's no saved amount to compare).
 */
export function shouldBlockZeroPrice(price, { prices, draftId, isNew, initialValues } = {}) {
  if (!isMultiPriceZeroBlocked(price, prices, draftId, isNew)) return false;
  if (isNew) return true;
  return isPriceAmountDirty(price, initialValues);
}

/**
 * Whether an add-price draft holds meaningful user input, so cancelling it
 * should warn first. A pristine draft (just the carried billing defaults, with
 * an empty amount/label) is not dirty.
 */
export function isPriceDraftDirty(draft) {
  if (!draft) return false;
  return [
    'amount',
    'label',
    'description',
    'badge',
    'pwywSuggestedPrice',
    'discountedFromPrice',
    'setupFee',
  ].some(key => draft[key] != null && String(draft[key]).trim() !== '');
}

/**
 * Reconcile a pending currency change across the product, called from a price's Save
 * handler before it persists. The currency Select only changes the edited price's
 * currency locally; if it now differs from the product currency, propagate it to the
 * product + every other price. Currency is product-wide, so changing one price changes
 * all — confirm first when there's more than one price.
 *
 * Uses path-based setFieldValue (`prices[i].currency`) so it doesn't clobber other
 * in-flight edits (e.g. the draft's amount). Returns false if the user cancels the
 * confirm, so the caller can abort the save.
 */
/**
 * Reconcile every price to a single currency for a commit that has no per-price
 * Save to run reconcileCurrencyOnSave (the create flow's "Add another price" and
 * "Create checkout"). A deferred currency change leaves one price on a currency
 * that differs from the product's; adopt it — confirming first on a multi-price
 * product — and return the product + every price set to it. Stripe requires one
 * currency per product, so this is what keeps the API from rejecting the save.
 *
 * Returns `{ currency, prices }` (unchanged when nothing differs), or `null` if the
 * user cancels the confirm so the caller can abort the commit.
 */
export async function reconcileCurrencyToSingle({ values, confirm }) {
  const prices = values?.prices || [];
  const differing = prices.find(p => p.currency && p.currency !== values?.currency);
  if (!differing) return { currency: values?.currency, prices };
  if (prices.length > 1) {
    const ok = await confirm('Change currency of all prices?', { confirm: 'Save' });
    if (!ok) return null;
  }
  const currency = differing.currency;
  return { currency, prices: prices.map(p => ({ ...p, currency })) };
}

export async function reconcileCurrencyOnSave({ values, setFieldValue, confirm, index }) {
  const prices = values?.prices || [];
  const newCurrency = prices[index]?.currency;
  // A currency change is deferred while editing — it only touched this price. On Save it
  // applies: propagate to the product currency + every other price. Confirm first on a
  // multi-price product; a single/first price already applied on change, so this no-ops.
  // Returns false to abort the save if the confirm is cancelled.
  if (!newCurrency || newCurrency === values?.currency) return true;
  if (prices.length > 1) {
    const ok = await confirm('Change currency of all prices?', { confirm: 'Save' });
    if (!ok) return false;
  }
  await setFieldValue('currency', newCurrency);
  for (let i = 0; i < prices.length; i += 1) {
    if (prices[i]?.currency !== newCurrency) {
      await setFieldValue(`prices[${i}].currency`, newCurrency);
    }
  }
  return true;
}
